<template>
  <v-container>
    <h3>アナログ入力チャンネル使用設定</h3>

    <v-form @submit.prevent ref="form" class="mt-8 mb-5">

      <v-row>
        <v-col :cols="$vuetify.display.mobile ? 12 : 4" class="d-flex align-center">
          <v-label>チャンネル選択</v-label>
        </v-col>
        <v-col :cols="$vuetify.display.mobile ? 12 : 8">
          <v-select density="compact" hide-details="true"
            v-model:model-value="channelKey" :items="channels"></v-select>
        </v-col>
      </v-row>

      <v-divider class="my-7"></v-divider>

      <v-row v-if="channelKey !== ''">
        <v-col :cols="$vuetify.display.mobile ? 12 : 4" class="d-flex align-center">
          <v-label>端子入力モード</v-label>
        </v-col>
        <v-col :cols="$vuetify.display.mobile ? 12 : 5">
          <v-chip class="my-3">{{ mode === 'mA' ? '4 - 20mA' : '0 - 10V' }}</v-chip>
        </v-col>

        <v-col :cols="$vuetify.display.mobile ? 12 : 4" class="d-flex align-center">
          <v-label>ステータス</v-label>
        </v-col>
        <v-col :cols="$vuetify.display.mobile ? 12 : 5">
          <v-chip :color="(isUsing) ? 'success' : ''" class="my-3">{{ isUsing ? '計測中' : '未使用' }}</v-chip>
        </v-col>

        <v-col cols="12" class="mt-4">
          <v-label>計測機器出力値範囲</v-label>
        </v-col>
        <v-col :cols="$vuetify.display.mobile ? 12 : 6">
          <v-text-field density="compact" :label="mode === 'mA' ? '最小値（mA）' : '最小値（V）'"
            :rules="[ v => (!!v) || '必須、数値', v => (v && !isNaN(Number(v))) || '必須、数値' ]"
            persistent-hint hint="必須、数値"
            v-model="sensorMin" :readonly="!isConfigurable"></v-text-field>
        </v-col>
        <v-col :cols="$vuetify.display.mobile ? 12 : 6">
          <v-text-field density="compact" :label="mode === 'mA' ? '最大値（mA）' : '最大値（V）'"
            :rules="[ v => (!!v) || '必須、数値', v => (v && !isNaN(Number(v))) || '必須、数値' ]"
            persistent-hint hint="必須、数値"
            v-model="sensorMax" :readonly="!isConfigurable"></v-text-field>
        </v-col>

        <v-col cols="12" class="mt-3">
          <v-label>対応物理値範囲</v-label>
        </v-col>
        <v-col :cols="$vuetify.display.mobile ? 12 : 6">
          <v-text-field density="compact" :label="'最小値（' + unit + '）'"
            :rules="isConfigurable ? [ v => (!!v) || '必須、数値', v => (v && !isNaN(Number(v))) || '必須、数値' ] : []"
            persistent-hint hint="必須、数値"
            v-model="physicalMin" :readonly="!isConfigurable"></v-text-field>
        </v-col>
        <v-col :cols="$vuetify.display.mobile ? 12 : 6">
          <v-text-field density="compact" :label="'最大値（' + unit + '）'"
            :rules="isConfigurable ? [ v => (!!v) || '必須、数値', v => (v && !isNaN(Number(v))) || '必須、数値' ] : []"
            persistent-hint hint="必須、数値"
            v-model="physicalMax" :readonly="!isConfigurable"></v-text-field>
        </v-col>

        <v-col cols="12">
          <v-btn variant="outlined" v-on:click="calcFormula" :disabled="!isConfigurable">変換式を算出</v-btn>
        </v-col>

        <v-col cols="12" class="mt-4">
          <v-label>変換式</v-label>
        </v-col>
        <v-col :cols="$vuetify.display.mobile ? 12 : 6">
          <v-text-field density="compact" label="ゲイン"
            :rules="isConfigurable ? [ v => (!!v) || '必須、数値', v => (v && !isNaN(Number(v))) || '必須、数値' ] : []"
            persistent-hint hint="必須、数値"
            v-model="gain" :readonly="!isConfigurable"></v-text-field>
        </v-col>
        <v-col :cols="$vuetify.display.mobile ? 12 : 6">
          <v-text-field density="compact" label="オフセット"
            :rules="isConfigurable ? [ v => (!!v) || '必須、数値', v => (v && !isNaN(Number(v))) || '必須、数値' ] : []"
            persistent-hint hint="必須、数値"
            v-model="offset" :readonly="!isConfigurable"></v-text-field>
        </v-col>

        <v-col cols="12" class="mt-3">
          <v-label>小数点以下桁数</v-label>
        </v-col>
        <v-col :cols="$vuetify.display.mobile ? 12 : 6">
          <v-select density="compact"
            persistent-hint hint="必須"
            v-model:model-value="decimalDigits" :readonly="!isConfigurable" :items="[
              { title: '0（整数値）', value: 0 },
              { title: '1（ex：0.1）', value: 1 },
              { title: '2（ex：0.01）', value: 2 },
              { title: '3（ex：0.001）', value: 3 },
              { title: '4（ex：0.0001）', value: 4 },
              { title: '5（ex：0.00001）', value: 5 }
            ]"></v-select>
        </v-col>

        <v-col cols="12" class="mt-4">
          <v-label>* 変換シミュレーション</v-label>
        </v-col>
        <v-col :cols="$vuetify.display.mobile ? 12 : 6">
          <v-text-field density="compact" :label="mode === 'mA' ? '入力電流値（mA）' : '入力電圧値（V）'"
            v-model="inputVal"></v-text-field>
        </v-col>
        <v-col :cols="$vuetify.display.mobile ? 12 : 6">
          <v-text-field density="compact" :label="'物理値（' + unit + '）'"
            v-model="physicalVal" readonly variant="underlined"></v-text-field>
        </v-col>

        <v-col cols="12" class="my-2">
          <v-alert variant="tonal" type="success" v-model="isShowStartMessage" closable>
            データ保存を開始しました
          </v-alert>

          <v-alert variant="tonal" type="success" v-model="isShowUpdateMessage" closable>
            変更しました
          </v-alert>

          <v-alert variant="tonal" type="success" v-model="isShowDeleteMessage" closable>
            データ保存が停止されました
          </v-alert>
        </v-col>

        <v-col cols="12" class="d-flex justify-space-between" v-if="isConfigurable">
          <v-btn variant="outlined" color="error" v-if="!$vuetify.display.mobile" v-bind:class="{ 'v-hidden': !isUsing }"
            :loading="isDeleteLoading" v-on:click="isShowDialog = true;">設定を削除して計測を終了</v-btn>

          <v-btn :elevation="0" color="primary" :loading="isPutLoading" v-on:click="putConvertParams">
            {{ isUsing ? '変更する' : '上記の設定で計測を開始' }}
          </v-btn>
        </v-col>

        <v-col cols="12" v-if="isConfigurable && $vuetify.display.mobile && isUsing">
          <v-btn variant="outlined" color="error"
            :loading="isDeleteLoading" v-on:click="isShowDialog = true;">設定を削除して計測を終了</v-btn>
        </v-col>
      </v-row>

    </v-form>

    <v-dialog max-width="400" v-model="isShowDialog">
      <v-card>
        <v-card-text>
          データ保存が停止されます。よろしいですか？
        </v-card-text>
        <v-card-actions class="d-flex justify-end">
          <v-btn v-on:click="isShowDialog = false;">
            キャンセル
          </v-btn>
          <v-btn color="error" v-on:click="isShowDialog = false; deleteConvertParams();">
            削除
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { defineComponent, ref, onMounted, watch } from 'vue';

import axios from 'axios';

import config from '@/config.js';

export default defineComponent({
  name: 'AnalogInputUsageSetting',
  props: {
    moduleId: { type: String },
    isConfigurable: { type: Boolean }
  },

  setup(props) {

    const form = ref();

    const channelKey = ref('');
    const channels = ref([
      { title: '未選択', value: '' }
    ]);

    const channelInfoList = ref({});

    const mode = ref('mA');
    const unit = ref('');

    const isUsing = ref(false);

    const sensorMin = ref('');
    const sensorMax = ref('');

    const physicalMin = ref('');
    const physicalMax = ref('');

    const gain = ref('');
    const offset = ref('');

    const decimalDigits = ref(0);

    const inputVal = ref('');
    const physicalVal = ref(' ');

    const isShowStartMessage = ref(false);
    const isShowUpdateMessage = ref(false);
    const isShowDeleteMessage = ref(false);

    const isPutLoading = ref(false);
    const isDeleteLoading = ref(false);

    const isShowDialog = ref(false);


    onMounted(() => {
      fetchChannelList();

      watch(channelKey, () => {
        sensorMin.value = '';
        sensorMax.value = '';

        physicalMin.value = '';
        physicalMax.value = '';

        gain.value = '';
        offset.value = '';

        decimalDigits.value = 0;

        inputVal.value = '';
        physicalVal.value = ' ';

        form.value.resetValidation();

        isShowStartMessage.value = false;
        isShowUpdateMessage.value = false;
        isShowDeleteMessage.value = false;

        if (channelKey.value === '') {
          return;
        }

        mode.value = channelInfoList.value[channelKey.value].mode;

        unit.value = channelInfoList.value[channelKey.value].unit;


        getConvertParams();
      });

      watch(gain, calcPhysicalValue);
      watch(offset, calcPhysicalValue);
      watch(decimalDigits, calcPhysicalValue);
      watch(inputVal, calcPhysicalValue);
    });

    const fetchChannelList = () => {
      axios.get(
        config.API_URL +
        `/modules/${ props.moduleId }/channels`,
        { headers: { Authorization: 'Bearer ' + window.sessionStorage.getItem('access-token') } }
      ).then((response) => {

        for (let key in response.data.channels) {
          if (key.startsWith('a')) {
            channels.value.push({
              title: '（アナログ' + key.substring(1) + '）' + response.data.channels[key].name,
              value: key.substring(1)
            });

            channelInfoList.value[key.substring(1)] = response.data.channels[key];
          }
        }

      }).catch((/* error */) => {
        // TBD リフレッシュトークンの利用を検討
        window.sessionStorage.clear();
        window.sessionStorage.setItem('access-token-expired', 'true');

        window.location.reload();
      });
    };

    const getConvertParams = () => {
      axios.get(
        config.API_URL +
        `/modules/${ props.moduleId }/channels/analog/${ channelKey.value }`,
        { headers: { Authorization: 'Bearer ' + window.sessionStorage.getItem('access-token') } }
      ).then((response) => {

        if (response.data.gain == null) {
          isUsing.value = false;

          if (mode.value === 'mA') {
            sensorMin.value = '4';
            sensorMax.value = '20';
          } else {
            sensorMin.value = '0';
            sensorMax.value = '10';
          }

          return;
        }

        isUsing.value = true;

        sensorMin.value = response.data.sensorMin.toString();
        sensorMax.value = response.data.sensorMax.toString();

        physicalMin.value = response.data.physicalMin.toString();
        physicalMax.value = response.data.physicalMax.toString();

        gain.value = response.data.gain.toString();
        offset.value = response.data.offset.toString();

        decimalDigits.value = response.data.decimalDigits;

      }).catch((/* error */) => {
        // TBD リフレッシュトークンの利用を検討
        window.sessionStorage.clear();
        window.sessionStorage.setItem('access-token-expired', 'true');

        window.location.reload();
      });
    };

    const calcFormula = () => {

      if (sensorMin.value === '') {
        return;
      }

      const sensorMinNum = Number(sensorMin.value);
      if (isNaN(sensorMinNum)) {
        return;
      }

      if (sensorMax.value === '') {
        return;
      }

      const sensorMaxNum = Number(sensorMax.value);
      if (isNaN(sensorMaxNum)) {
        return;
      }

      if (physicalMin.value === '') {
        return;
      }

      const physicalMinNum = Number(physicalMin.value);
      if (isNaN(physicalMinNum)) {
        return;
      }

      if (physicalMax.value === '') {
        return;
      }

      const physicalMaxNum = Number(physicalMax.value);
      if (isNaN(physicalMaxNum)) {
        return;
      }


      const gainNum = (physicalMaxNum - physicalMinNum) / (sensorMaxNum - sensorMinNum);
      if (isNaN(gainNum)) {
        return;
      }

      const offsetNum = (-1 * gainNum * sensorMinNum) + physicalMinNum;
      if (isNaN(offsetNum)) {
        return;
      }

      gain.value = gainNum.toString();
      offset.value = offsetNum.toString();

    };

    const calcPhysicalValue = () => {

      physicalVal.value = ' ';


      if (gain.value === '') {
        return;
      }

      const gainNum = Number(gain.value);
      if (isNaN(gainNum)) {
        return;
      }

      if (offset.value === '') {
        return;
      }

      const offsetNum = Number(offset.value);
      if (isNaN(offsetNum)) {
        return;
      }

      if (inputVal.value === '') {
        return;
      }

      const inputValNum = Number(inputVal.value);
      if (isNaN(inputValNum)) {
        return;
      }


      let physicalValNum = inputValNum * gainNum + offsetNum;

      const exp = 10 ** decimalDigits.value;
      physicalValNum = Math.round(physicalValNum * exp) / exp;

      physicalVal.value = physicalValNum.toString();

    };

    const putConvertParams = async () => {

      const { valid } = await form.value.validate();
      if (!valid) {
        return;
      }

      isPutLoading.value = true;

      isShowStartMessage.value = false;
      isShowUpdateMessage.value = false;
      isShowDeleteMessage.value = false;

      const reqBody = {
        sensorMin: Number(sensorMin.value),
        sensorMax: Number(sensorMax.value),
        physicalMin: Number(physicalMin.value),
        physicalMax: Number(physicalMax.value),
        gain: Number(gain.value),
        offset: Number(offset.value),
        decimalDigits: decimalDigits.value
      };

      axios.put(
        config.API_URL +
        `/modules/${ props.moduleId }/channels/analog/${ channelKey.value }`,
        reqBody,
        { headers: { Authorization: 'Bearer ' + window.sessionStorage.getItem('access-token') } }
      ).then((/* response */) => {

        if (isUsing.value) {
          isShowUpdateMessage.value = true;
        } else {
          isShowStartMessage.value = true;
        }

        isUsing.value = true;

        isPutLoading.value = false;

      }).catch((/* error */) => {
        // TBD リフレッシュトークンの利用を検討
        window.sessionStorage.clear();
        window.sessionStorage.setItem('access-token-expired', 'true');

        window.location.reload();
      });

    };

    const deleteConvertParams = () => {

      isDeleteLoading.value = true;

      isShowStartMessage.value = false;
      isShowUpdateMessage.value = false;

      axios.delete(
        config.API_URL +
        `/modules/${ props.moduleId }/channels/analog/${ channelKey.value }`,
        { headers: { Authorization: 'Bearer ' + window.sessionStorage.getItem('access-token') } }
      ).then((/* response */) => {

        isUsing.value = false;

        if (mode.value === 'mA') {
          sensorMin.value = '4';
          sensorMax.value = '20';
        } else {
          sensorMin.value = '0';
          sensorMax.value = '10';
        }

        physicalMin.value = '';
        physicalMax.value = '';

        gain.value = '';
        offset.value = '';

        decimalDigits.value = 0;

        inputVal.value = '';
        physicalVal.value = ' ';

        form.value.resetValidation();

        isShowDeleteMessage.value = true;

        isDeleteLoading.value = false;

      }).catch((/* error */) => {
        // TBD リフレッシュトークンの利用を検討
        window.sessionStorage.clear();
        window.sessionStorage.setItem('access-token-expired', 'true');

        window.location.reload();
      });

    };

    return {
      form,

      channelKey,
      channels,

      mode,
      unit,

      isUsing,

      sensorMin,
      sensorMax,

      physicalMin,
      physicalMax,

      gain,
      offset,

      decimalDigits,

      inputVal,
      physicalVal,

      isShowStartMessage,
      isShowUpdateMessage,
      isShowDeleteMessage,

      isPutLoading,
      isDeleteLoading,

      isShowDialog,

      calcFormula,
      putConvertParams,
      deleteConvertParams
    }

  }
});
</script>
